import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xbgjkjpg";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          ...form,
          _subject: `New website inquiry from ${form.name}`,
        }),
      });
      if (response.ok) {
        toast.success("Thanks! Your message has been sent.");
        setForm({ name: "", email: "", message: "" });
      } else {
        const data = await response.json().catch(() => null);
        const msg =
          data?.errors?.map((err) => err.message).join(", ") ||
          "Couldn't send right now. Please try again.";
        toast.error(msg);
      }
    } catch (err) {
      toast.error("Couldn't send right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="overflow-hidden">
      <SEO
        title="Contact Us"
        description="Contact Innovators Prep at 4312 Church Ave, Brooklyn, NY — ask about enrollment, schedule a visit, or reach our 4410 Preschool Special Education team."
      />
      <PageHero title="Contact" crumb="Contact" />

      <section className="relative py-20 md:py-28">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-10">
          {/* Form */}
          <Reveal>
            <div className="bg-white rounded-[2.5rem] shadow-lg border border-gray-100 p-8 md:p-10 h-full">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
                Get in touch
              </span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-gray-900">
                School Contact
              </h2>
              <p className="mt-3 text-gray-600">
                Have a question, would like to schedule a visit, or interested in enrollment? Send us a
                note and we'll be in touch soon.
              </p>

              <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold text-gray-700">
                    Your Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-name"
                    placeholder="Jane Doe"
                    value={form.name}
                    onChange={onChange}
                    className="mt-1.5 rounded-2xl border-gray-200 h-12 focus-visible:ring-brand-green"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                    Your Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={onChange}
                    className="mt-1.5 rounded-2xl border-gray-200 h-12 focus-visible:ring-brand-green"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-sm font-semibold text-gray-700">
                    Your Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message"
                    rows={5}
                    placeholder="How can we help?"
                    value={form.message}
                    onChange={onChange}
                    className="mt-1.5 rounded-2xl border-gray-200 focus-visible:ring-brand-green"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-deep text-white rounded-full px-7 py-6 text-base font-semibold shadow-md hover:-translate-y-0.5 transition-transform"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Now
                </Button>
              </form>
            </div>
          </Reveal>

          {/* Info + map */}
          <Reveal delay={0.15}>
            <div className="bg-gradient-to-br from-brand-purple-soft via-white to-brand-green-soft rounded-[2.5rem] shadow-lg border border-gray-100 p-8 md:p-10 h-full">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-purple text-white text-xs font-bold tracking-widest uppercase">
                4410 Program
              </span>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold text-gray-900">
                Contact Preschool Special Education Program (4410)
              </h2>

              <ul className="mt-7 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="h-12 w-12 rounded-2xl bg-white shadow flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-brand-green-deep" />
                  </span>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Address</div>
                    <div className="font-semibold text-gray-900">4312 Church Ave, Brooklyn, NY 11203</div>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="h-12 w-12 rounded-2xl bg-white shadow flex items-center justify-center">
                    <Phone className="h-5 w-5 text-brand-green-deep" />
                  </span>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Phone</div>
                    <a href="tel:+17182823500" className="font-semibold text-gray-900 hover:text-brand-green-deep">
                      +1 (718) 282-3500
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="h-12 w-12 rounded-2xl bg-white shadow flex items-center justify-center">
                    <Mail className="h-5 w-5 text-brand-green-deep" />
                  </span>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Email</div>
                    <a
                      href="mailto:Innovatorsprep1@gmail.com"
                      className="font-semibold text-gray-900 hover:text-brand-green-deep break-all"
                    >
                      Innovatorsprep1@gmail.com
                    </a>
                  </div>
                </li>
              </ul>

              <div
                className="mt-8 rounded-[2rem] overflow-hidden shadow border border-white"
                data-testid="contact-map"
              >
                <iframe
                  title="Innovators Prep Location"
                  src="https://www.google.com/maps?q=4312+Church+Ave,+Brooklyn,+NY+11203&output=embed"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
